import React, { useState, useEffect, useRef } from "react";
import { API } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Loader2, Edit2, PenLine, Upload, X, ShieldCheck, Building2 } from "lucide-react";
import { SignatureBlock } from "@/components/staff/SignatureBlock";

const EMPTY = { full_name: "", professional_title: "" };
const FIELDS = [["full_name", "Full name *", "e.g. Adaeze Okafor"], ["professional_title", "Professional title *", "e.g. Registered Dietitian"]];

const SignatureForm = ({ initial, token, onSaved, onCancel }) => {
  const [f, setF] = useState(initial ? { ...EMPTY, ...initial } : EMPTY);
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(Object.keys(EMPTY).map(k => [k, f[k] || ""]));
      const r = await fetch(`${API}/staff/signatures${initial ? `/${initial.signature_id}` : ""}`, { method: initial ? "PUT" : "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const d = await r.json();
      if (!r.ok) { toast.error(d.detail || "Could not save."); return; }
      toast.success("Signature saved"); onSaved();
    } catch { toast.error("Connection error"); } finally { setSaving(false); }
  };
  return (
    <Card className="rounded-xl border-[#FF7A00]/30 p-5 space-y-4" data-testid="signature-form">
      <h3 className="text-sm font-bold text-stone-900">{initial ? "Edit signature" : "New dietitian signature"}</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        {FIELDS.map(([k, label, ph]) => (
          <div key={k}>
            <label className="text-xs font-semibold text-stone-600 mb-1 block">{label}</label>
            <Input value={f[k] || ""} onChange={e => setF({ ...f, [k]: e.target.value })} placeholder={ph} className="rounded-lg" data-testid={`sig-${k}`} />
          </div>
        ))}
      </div>
      <p className="text-[11px] text-stone-500">Full name and professional title appear under the signature. You can add a handwritten signature image after saving.</p>
      <div className="flex gap-2">
        <Button onClick={save} disabled={saving} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg" data-testid="sig-save">{saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save signature</Button>
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </Card>
  );
};

const SignatureCard = ({ sig, token, onEdit, reload }) => {
  const fileRef = useRef(null);
  const team = sig.signature_type === "team";
  const h = { Authorization: `Bearer ${token}` };
  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const r = await fetch(`${API}/staff/signatures/${sig.signature_id}/image`, { method: "POST", headers: h, body: fd });
    const d = await r.json();
    r.ok ? toast.success(d.message) : toast.error(d.detail || "Upload failed"); reload();
  };
  const call = async (url, method = "POST") => { const r = await fetch(url, { method, headers: h }); const d = await r.json(); r.ok ? toast.success(d.message) : toast.error(d.detail); reload(); };
  return (
    <Card className={`rounded-xl p-4 border-stone-200/70 ${!sig.active ? "opacity-60" : ""}`} data-testid={`signature-card-${sig.signature_id}`}>
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex gap-1.5 flex-wrap">
          {team ? <Badge className="text-[10px] bg-stone-900 text-white"><Building2 className="w-3 h-3 mr-1" />Organisation</Badge> : <Badge variant="outline" className="text-[10px]">Dietitian</Badge>}
          {!sig.active && <Badge variant="outline" className="text-[10px] text-stone-500">Inactive</Badge>}
          {sig.doctor_ready && <Badge className="text-[10px] bg-green-50 text-green-700 border-green-200"><ShieldCheck className="w-3 h-3 mr-1" />Doctor reports</Badge>}
        </div>
        {!team && <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => onEdit(sig)} data-testid={`sig-edit-${sig.signature_id}`}><Edit2 className="w-3.5 h-3.5" /></Button>}
      </div>
      <SignatureBlock sig={sig} token={token} />
      {team ? <p className="text-[11px] text-stone-400 mt-3">Used for newsletters, reminders and general emails. Always available.</p> : (<>
        {sig.missing_for_doctor?.length > 0 && <p className="text-[11px] text-amber-700 mt-3">Add {sig.missing_for_doctor.join(", ")} to use on doctor reports.</p>}
        <div className="flex flex-wrap gap-2 mt-3">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} data-testid={`sig-image-input-${sig.signature_id}`} />
          <Button size="sm" variant="outline" className="h-7 text-xs rounded-lg" onClick={() => fileRef.current?.click()} data-testid={`sig-image-${sig.signature_id}`}><Upload className="w-3 h-3 mr-1" />{sig.has_image ? "Replace" : "Add"} handwritten signature</Button>
          {sig.has_image && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => call(`${API}/staff/signatures/${sig.signature_id}/image`, "DELETE")} data-testid={`sig-image-remove-${sig.signature_id}`}><X className="w-3 h-3 mr-1" />Remove image</Button>}
          <Button size="sm" variant="ghost" className="h-7 text-xs ml-auto" onClick={() => call(`${API}/staff/signatures/${sig.signature_id}/active?active=${!sig.active}`)} data-testid={`sig-toggle-${sig.signature_id}`}>{sig.active ? "Deactivate" : "Reactivate"}</Button>
        </div></>)}
    </Card>
  );
};

export const SignaturesManager = ({ token }) => {
  const [sigs, setSigs] = useState(null);
  const [form, setForm] = useState(null);
  const load = () => fetch(`${API}/staff/signatures`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).then(d => setSigs(d.signatures || []));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-4" data-testid="signatures-tab">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-bold text-stone-900 flex items-center gap-2"><PenLine className="w-5 h-5 text-[#FF7A00]" />Signatures</h3>
          <p className="text-xs text-stone-500 mt-1">When you send or download a clinical report or meal plan, you'll choose one of these. Reports for doctors must use a named dietitian.</p>
        </div>
        {!form && <Button onClick={() => setForm("new")} className="bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-lg shrink-0" data-testid="add-signature-btn"><Plus className="w-4 h-4 mr-1" />Add signature</Button>}
      </div>
      {form && <SignatureForm key={form === "new" ? "new" : form.signature_id} initial={form === "new" ? null : form} token={token} onSaved={() => { setForm(null); load(); }} onCancel={() => setForm(null)} />}
      {!sigs ? <Loader2 className="w-6 h-6 animate-spin text-stone-400" /> : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">{sigs.map(s => <SignatureCard key={s.signature_id} sig={s} token={token} onEdit={setForm} reload={load} />)}</div>
      )}
    </div>
  );
};
